<template>
    <div class="inertia-tool">
        <div class="form__item">
            <label>
                <span>{{ $t('inertiaTool.labelSpan') }}</span>
                <a class="label-text--second-line pull-right" @click="$emit('toggle')">
                    {{ $t('inertiaTool.labelLink') }}
                </a>
            </label>

            <div v-if="loadInertiaAbstractions" class="inertia-tool__abstractions-selector">
                <div
                    v-for="(abstraction, index) in loadInertiaAbstractions"
                    :key="abstraction.type.id + index"
                    class="inertia-tool__abstractions-selector__abstraction"
                    :class="{
                        'inertia-tool__abstractions-selector__abstraction--active':
                            selectedLoadInertiaAbstraction === index,
                    }"
                    @click="selectAbstraction(index)"
                >
                    {{ index + 1 }}
                </div>
                <div
                    class="inertia-tool__abstractions-selector__abstraction"
                    @click="addAbstraction()"
                >
                    +
                </div>
            </div>
        </div>

        <div v-if="loadInertiaAbstraction" class="inertia-tool__abstraction">
            <div class="form__item">
                <div class="row">
                    <div class="col-4">
                        <label>
                            <span>{{ $t('inertiaTool.model') }}</span>
                        </label>
                    </div>
                    <div class="col-8">
                        <icon-link
                            v-if="selectedLoadInertiaAbstraction > 0"
                            @click.native="deleteAbstraction()"
                            iconName="mdi-delete"
                            class="pull-right inertia-tool__abstraction__delete-btn"
                        >
                            {{ $t('inertiaTool.deleteModel') }}
                        </icon-link>
                    </div>
                </div>

                <div class="select select--input-margins">
                    <select :value="loadInertiaAbstraction.type.id" @input="changeAbstractionType">
                        <option
                            v-for="type in loadInertiaAbstractionTypes"
                            :key="type.id"
                            :value="type.id"
                        >
                            {{ type.name }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="row inertia-tool__solid-hollow">
                <div class="offset-1 col-5 radio">
                    <input
                        class="radio__rb"
                        type="radio"
                        name="solidHollow"
                        id="solid"
                        value="solid"
                        v-model="scenario.applicationData.loadInertiaSolidHollow"
                        @change="computeLoadInertiaAbstraction"
                        :disabled="!loadInertiaAbstraction.type.hasSolid"
                    />
                    <label class="radio__label" for="solid">{{ $t('inertiaTool.solid') }}</label>
                </div>
                <div class="offset-1 col-5 radio">
                    <input
                        class="radio__rb"
                        type="radio"
                        name="solidHollow"
                        id="hollow"
                        value="hollow"
                        v-model="scenario.applicationData.loadInertiaSolidHollow"
                        @change="computeLoadInertiaAbstraction"
                        :disabled="!loadInertiaAbstraction.type.hasHollow"
                    />
                    <label class="radio__label" for="hollow">{{ $t('inertiaTool.hollow') }}</label>
                </div>
            </div>

            <div class="row">
                <img
                    class="col-6"
                    style="margin-top: 2rem"
                    :src="getLoadInertiaAbstractionImage(loadInertiaAbstraction.type.id)"
                />

                <div class="col-6">
                    <div class="form__item">
                        <label>{{ $t('inertiaTool.mass') }}</label>
                        <input
                            type="text"
                            ref="loadInertiaMass"
                            v-model.lazy="scenario.applicationData.loadInertiaMass"
                            @blur="updateLoadInertiaMass"
                            @change="computeLoadInertiaAbstraction"
                            class="form__input--text"
                        />
                    </div>
                    <div class="form__item" v-if="loadInertiaAbstraction.type.formula.length === 3">
                        <label>{{ $t('inertiaTool.radius') }}</label>
                        <input
                            type="text"
                            ref="loadInertiaRadius"
                            v-model.lazy="scenario.applicationData.loadInertiaRadius"
                            @blur="updateLoadInertiaRadius"
                            @change="computeLoadInertiaAbstraction"
                            class="form__input--text"
                        />
                    </div>

                    <div class="form__item" v-if="loadInertiaAbstraction.type.formula.length === 4">
                        <label>{{ $t('inertiaTool.width') }}</label>
                        <input
                            type="text"
                            ref="loadInertiaWidth"
                            v-model.lazy="scenario.applicationData.loadInertiaWidth"
                            @blur="updateLoadInertiaWidth"
                            @change="computeLoadInertiaAbstraction"
                            class="form__input--text"
                        />
                    </div>

                    <div class="form__item" v-if="loadInertiaAbstraction.type.formula.length === 4">
                        <label>{{ $t('inertiaTool.length') }}</label>
                        <input
                            type="text"
                            ref="loadInertiaLength"
                            v-model.lazy="scenario.applicationData.loadInertiaLength"
                            @blur="updateLoadInertiaLength"
                            @change="computeLoadInertiaAbstraction"
                            class="form__input--text"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-6 form__item">
                <label>
                    <span>{{ $t('inertiaTool.resulting') }}</span>
                </label>
                <input
                    type="text"
                    ref="loadInertia"
                    v-model.lazy="scenario.applicationData.loadInertia"
                    @blur="updateLoadInertia"
                    class="form__input--text"
                    disabled
                />
            </div>

            <div class="col-sm-6">
                <a class="button--secondary" style="margin-top: 3.2rem;" @click="$emit('toggle')">
                    {{ $t('inertiaTool.buttonToggle') }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { LOAD_INERTIA_TYPES } from '../models/LoadInertiaAbstractionTypes';

@Component
export default class InertiaTool extends Vue {
    @Prop() scenario;

    loadInertiaAbstractions;
    selectedLoadInertiaAbstraction;
    loadInertiaAbstractionTypes = LOAD_INERTIA_TYPES;

    created() {
        // Set defaults for new scenarios
        if (!this.scenario.applicationData.loadInertiaAbstractions) {
            this.scenario.applicationData.loadInertiaAbstractions = [
                { type: this.loadInertiaAbstractionTypes[0], values: {} },
            ];
        }

        if (this.scenario.applicationData.selectedLoadInertiaAbstraction === undefined) {
            this.scenario.applicationData.selectedLoadInertiaAbstraction = 0;
        }

        this.loadInertiaAbstractions = this.scenario.applicationData.loadInertiaAbstractions;
        this.selectedLoadInertiaAbstraction = this.scenario.applicationData.selectedLoadInertiaAbstraction;
    }

    mounted() {
        this.computeLoadInertiaAbstraction();
    }

    selectAbstraction(abstractionIndex) {
        if (this.selectedLoadInertiaAbstraction == abstractionIndex) return;
        this.selectedLoadInertiaAbstraction = abstractionIndex;
        this.computeLoadInertiaAbstraction();
    }

    addAbstraction() {
        this.loadInertiaAbstractions.push({
            type: this.loadInertiaAbstractionTypes[0],
            values: {},
        });
        this.selectedLoadInertiaAbstraction = this.loadInertiaAbstractions.length - 1;
        this.computeLoadInertiaAbstraction();
    }

    deleteAbstraction() {
        this.loadInertiaAbstractions.splice(this.selectedLoadInertiaAbstraction, 1);
        this.selectedLoadInertiaAbstraction = Math.max(0, this.selectedLoadInertiaAbstraction - 1);
        this.computeLoadInertiaAbstraction();
    }

    /**
     * Set the updated value as soon as an input blurs to reflect the unit
     * @param field
     */
    onInputBlur(field, value) {
        if (field !== undefined) {
            field.value = value;
        }
    }

    /**
     * Get a load inertia abstraction image based on id
     */
    getLoadInertiaAbstractionImage(abstractionId) {
        return '/img/inertia-' + abstractionId + '.svg';
    }

    updateLoadInertia() {
        this.onInputBlur(this.$refs.loadInertia, this.scenario.applicationData.loadInertia);
    }

    updateLoadInertiaMass() {
        this.onInputBlur(this.$refs.loadInertiaMass, this.scenario.applicationData.loadInertiaMass);
    }

    updateLoadInertiaRadius() {
        this.onInputBlur(
            this.$refs.loadInertiaRadius,
            this.scenario.applicationData.loadInertiaRadius,
        );
    }

    updateLoadInertiaWidth() {
        this.onInputBlur(
            this.$refs.loadInertiaWidth,
            this.scenario.applicationData.loadInertiaWidth,
        );
    }

    updateLoadInertiaLength() {
        this.onInputBlur(
            this.$refs.loadInertiaLength,
            this.scenario.applicationData.loadInertiaLength,
        );
    }

    changeAbstractionType(event) {
        this.loadInertiaAbstractions[
            this.selectedLoadInertiaAbstraction
        ].type = this.loadInertiaAbstractionTypes.find(
            abstractionType => abstractionType.id === event.target.value,
        );
        this.computeLoadInertiaAbstraction();
    }

    /**
     * Every change in the parameters used to estimate the loadInertia triggers
     * a value update.
     */
    computeLoadInertiaAbstraction(event) {
        this.scenario.applicationData.loadInertiaAbstractions = this.loadInertiaAbstractions;
        this.scenario.applicationData.selectedLoadInertiaAbstraction = this.selectedLoadInertiaAbstraction;
        this.scenario.applicationData.recomputeLoadInertia();
        this.updateLoadInertia();
    }

    get loadInertiaAbstraction() {
        if (!this.scenario.applicationData.loadInertiaAbstractions) return undefined;
        return this.scenario.applicationData.loadInertiaAbstractions[
            this.scenario.applicationData.selectedLoadInertiaAbstraction
        ];
    }
}
</script>
